/**
 * OfferToNeedModal — offer one of the user's resources against a community
 * need (DEV-1149), launched from the Resources match feed's "Offer" button.
 *
 * On confirm it calls the existing offer handshake
 * (POST /api/UserResources/{guid}/offer); the project lead reviews and accepts
 * before anything is committed.
 *
 * NOTE: this is the need→resource direction (given a need, pick which of your
 * resources to offer). The existing OfferResourceModal is the opposite
 * (resource→need) used by the Impact growth surface — kept separate.
 *
 * The offer payload has no offer-type field, so loan/donation/either is shown
 * read-only from the chosen resource (the handshake doesn't capture a choice
 * for 'either' resources — backend follow-up).
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { CommunityNeedItem } from '../../api/communityNeeds';
import type { OfferResourcePayload } from '../../api/userResources';
import {
  MATCHABLE_USER_STATES,
  OFFER_EXPIRATION_PRESETS,
  DEFAULT_OFFER_EXPIRATION_DAYS,
  type UserResource,
  type OfferExpirationDays,
} from '../../Data/UserResources/userResourceTypes';
import styles from './OfferToNeedModal.module.css';

interface OfferToNeedModalProps {
  need: CommunityNeedItem;
  resources: UserResource[];
  defaultResourceGuid: string | null;
  onConfirm: (resourceGuid: string, payload: OfferResourcePayload) => Promise<void>;
  onClose: () => void;
}

export default function OfferToNeedModal({
  need,
  resources,
  defaultResourceGuid,
  onConfirm,
  onClose,
}: OfferToNeedModalProps) {
  const { t } = useAppTranslation('userProfile');

  const available = useMemo(
    () => resources.filter((r) => MATCHABLE_USER_STATES.includes(r.state)),
    [resources]
  );

  const initialGuid = useMemo(() => {
    if (defaultResourceGuid && available.some((r) => r.resource_guid === defaultResourceGuid)) {
      return defaultResourceGuid;
    }
    return available[0]?.resource_guid ?? '';
  }, [defaultResourceGuid, available]);

  const [resourceGuid, setResourceGuid] = useState(initialGuid);
  const [expirationDays, setExpirationDays] = useState<OfferExpirationDays>(DEFAULT_OFFER_EXPIRATION_DAYS);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = available.find((r) => r.resource_guid === resourceGuid) || null;

  const handleSubmit = useCallback(async () => {
    if (!chosen) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm(chosen.resource_guid, {
        offeredItemGuid: need.item_guid,
        projectGuid: need.project_guid,
        offerNotes: note.trim() || undefined,
        expirationDays,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('resources.offerError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [chosen, need.item_guid, need.project_guid, note, expirationDays, onConfirm, onClose, t]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('resources.offerModalTitle')}</h3>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className={styles.subtitle}>{t('resources.offerModalSubtitle')}</p>

        {available.length === 0 ? (
          <div className={styles.empty}>{t('resources.offerNoAvailable')}</div>
        ) : (
          <div className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label}>{t('resources.offerResourceToOffer')}</label>
              <select className={styles.select} value={resourceGuid} onChange={(e) => setResourceGuid(e.target.value)}>
                {available.map((r) => (
                  <option key={r.resource_guid} value={r.resource_guid}>{r.label}</option>
                ))}
              </select>
              {chosen && (
                <span className={styles.offerTypeChip}>{t(`resources.offerTypes.${chosen.offer_type}`)}</span>
              )}
            </div>

            <div className={styles.needContext}>
              <span className={styles.needContextLabel}>{t('resources.offerFillsNeed')}</span>
              <span className={styles.needContextValue}>{need.label}</span>
              <span className={styles.needContextProject}>{need.project_name}</span>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{t('resources.offerExpiresIn')}</label>
              <div className={styles.seg}>
                {OFFER_EXPIRATION_PRESETS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`${styles.segBtn} ${expirationDays === d ? styles.segBtnOn : ''}`}
                    onClick={() => setExpirationDays(d)}
                  >
                    {t('resources.offerExpiryDays', { days: d })}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{t('resources.offerNoteLabel')}</label>
              <textarea
                className={styles.textarea}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('resources.offerNotePlaceholder')}
                rows={3}
              />
            </div>

            {error && <div className={styles.error}>{error}</div>}
          </div>
        )}

        <div className={styles.footer}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>{t('resources.cancel')}</button>
          <button
            type="button"
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!chosen || isSubmitting}
          >
            {isSubmitting ? t('resources.offerSending') : t('resources.offerSend')}
          </button>
        </div>
      </div>
    </div>
  );
}
